import { useState } from 'react';
import { css } from '@emotion/react';
import { colors, spacing, fontStyles } from '../../styles';
import PriceRange from '../PriceRange';
import PaywallAnimationsDemo, { PaywallAnimationType } from '../PaywallAnimations';

type PlaygroundTab = 'paywall-animations' | 'price-range';

const PlaygroundPage = () => {
  const [activeTab, setActiveTab] = useState<PlaygroundTab>('paywall-animations');
  const [selectedAnimation, setSelectedAnimation] = useState<PaywallAnimationType | null>(null);

  const handleAnimationSelect = (type: PaywallAnimationType) => {
    setSelectedAnimation(type);
    alert(
      `Selected: ${type}\n\nYou can now implement this animation for your paywalled Market Pulse and Market Summary sections.`,
    );
  };

  return (
    <div
      css={css`
        min-height: 100vh;
        background: var(--background-color);
        padding: ${spacing.deci};
        padding-bottom: 100px;
      `}
    >
      {/* Tab selector */}
      <div
        css={css`
          display: flex;
          gap: ${spacing.centi};
          margin-bottom: ${spacing.deci};
          border-bottom: 1px solid ${colors.cloud};
          padding-bottom: ${spacing.centi};

          html[data-theme='dark'] & {
            border-color: ${colors.tungsten};
          }
        `}
      >
        {[
          { id: 'paywall-animations' as const, label: 'Paywall Animations' },
          { id: 'price-range' as const, label: 'Price Range' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            css={css`
              ${fontStyles.mouse}
              padding: ${spacing.centi} ${spacing.deci};
              border: none;
              background: ${activeTab === tab.id ? colors.petrolBlue : 'transparent'};
              color: ${activeTab === tab.id ? 'white' : colors.silver};
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.2s;

              &:hover {
                background: ${activeTab === tab.id ? colors.petrolBlue : colors.fog};
              }

              html[data-theme='dark'] & {
                background: ${activeTab === tab.id ? colors.skyBlue : 'transparent'};
                color: ${activeTab === tab.id ? colors.liquorice : colors.tungsten};

                &:hover {
                  background: ${activeTab === tab.id ? colors.skyBlue : '#333'};
                }
              }
            `}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'paywall-animations' && (
        <div>
          {selectedAnimation && (
            <div
              css={css`
                background: ${colors.leafAir};
                border: 1px solid ${colors.leaf};
                border-radius: 8px;
                padding: ${spacing.deci};
                margin-bottom: ${spacing.deci};
                ${fontStyles.cat}
                color: ${colors.leaf};

                html[data-theme='dark'] & {
                  background: ${colors.leaf}20;
                }
              `}
            >
              ✅ Selected: <strong>{selectedAnimation}</strong>
            </div>
          )}
          <PaywallAnimationsDemo onSelect={handleAnimationSelect} />
        </div>
      )}

      {activeTab === 'price-range' && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${spacing.deci};
          `}
        >
          <h2
            css={css`
              ${fontStyles.shark}
              color: var(--text-color);
              margin: 0;
            `}
          >
            Price Range Component
          </h2>
          <div
            css={css`
              width: 114px;
            `}
          >
            <PriceRange highPrice={95000} lowPrice={847.2} markedPrice={50000} />
          </div>
          <div
            css={css`
              width: 114px;
            `}
          >
            <PriceRange highPrice={959999} lowPrice={959000} markedPrice={959600} />
          </div>
          <div
            css={css`
              width: 114px;
            `}
          >
            <PriceRange highPrice={100} lowPrice={0} markedPrice={80} />
          </div>
          <div
            css={css`
              width: 114px;
            `}
          >
            <PriceRange highPrice={0.4} lowPrice={0.05} markedPrice={0.2} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaygroundPage;
